import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, computed, effect, input, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EnpsControlComponent } from '@common/controls/enps-control/enps-control';
import { StoryOutputType } from '@src/suite/common/enums/enums';

import { StoryIconComponent } from '../../components/icon/story-icon';
import { PSTFieldNames } from '../../data-models/fields/pst-field-names';
import { StoryIconDirective } from '../../directives/story-icon';
import { GENERIC_STORY_OUTPUT_TYPE } from '../../enums/constants';
import { NON_BREAKING_SPACE_REGEX } from '../../utils/util';

@Component({
    selector: 'st-story-output-type',
    imports: [FormsModule, MatChipsModule, MatListModule, MatMenuModule, MatTooltipModule, StoryIconComponent, StoryIconDirective],
    templateUrl: './story-output-type.html',
    styleUrl: './story-output-type.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-story-output-type',
    },
})
export class StoryOutputTypeComponent extends EnpsControlComponent<LookupValue> {
    readonly menuTrigger = viewChild(MatMenuTrigger);
    override readonly value = input<any, LookupValue | undefined>(GENERIC_STORY_OUTPUT_TYPE, {
        transform: (value: LookupValue) => {
            if (value) {
                return { id: value?.id?.replace(NON_BREAKING_SPACE_REGEX, ' '), title: value?.title?.replace(NON_BREAKING_SPACE_REGEX, ' ') };
            }
            return GENERIC_STORY_OUTPUT_TYPE;
        },
    });
    readonly internalValue = signal<LookupValue[]>([GENERIC_STORY_OUTPUT_TYPE]);
    readonly gridMode = input(false, {
        transform: coerceBooleanProperty,
    });
    readonly addGridPopupClass = input(false, {
        transform: coerceBooleanProperty,
    });

    readonly record = input<StorytellingStoryRecord>();
    readonly showIcon = input(true, {
        transform: coerceBooleanProperty,
    });
    readonly showLabel = input(false, {
        transform: coerceBooleanProperty,
    });
    readonly mediumIcon = input(false, {
        transform: coerceBooleanProperty,
    });

    readonly storyOutputTypes = computed(() => {
        return this.globalTablesService.getListItemsForField(PSTFieldNames.PSTStoryOutputType) || [];
    });

    readonly menuClass = computed(() => {
        return this.addGridPopupClass() ? 'st-story-output-type-menu ag-custom-component-popup' : 'st-story-output-type-menu';
    });

    readonly selectedTypeLabel = computed(() => {
        return this.internalValue()?.[0]?.title ?? this.languageSettings.PSTWebListValue_PSTStoryOutputType_Generic;
    });

    readonly selectedType = computed(() => {
        return this.internalValue()?.[0]?.id ?? StoryOutputType.GENERIC;
    });

    readonly isDisabled = computed(() => {
        if (this.record()?.Path?.isWasteBinPath /*|| this.record?.parentRecord?.path?.isWasteBinPath*/) {
            return true;
        }
        return false;
    });

    readonly hasValue = computed(() => {
        return !!this.internalValue() && this.internalValue().length > 0;
    });

    constructor() {
        super();
        effect(() => {
            const currentValue = this.value();
            this.internalValue.set(currentValue ? [currentValue] : []);
        });
    }

    compareFunc(item1: LookupValue, item2: LookupValue) {
        return item1.id === item2.id;
    }

    internalValueChanged(event: MatSelectionListChange) {
        this.isValueChanged.set(true);
        this.internalValue.set([event.options[0].value]);
        this.menuTrigger()?.closeMenu();
    }

    valueChanged() {
        if (this.isValueChanged()) {
            this.isValueChanged.set(false);
            this.changed.emit(this.internalValue()[0]);
        }
    }

    onStoryTypeMenuClosed() {
        this.valueChanged();
        this.onMenuClosed();
    }

    onStoryTypeMenuOpened() {
        this.onMenuOpened();
    }

    openMatMenu() {
        if (!this.menuTrigger()?.menuOpen) {
            this.menuTrigger()?.openMenu();
        }
    }
}
